import { useCallback, useLayoutEffect, useRef, useState } from 'react';

type Options = {
  /**
   * Measure once after mount as well as on demand. Only worth it when the
   * answer drives something the reader must see without hovering first — an
   * affordance that pops into place on hover reads as a glitch.
   */
  measureOnMount?: boolean;
};

/**
 * Tracks whether an element's content is wider than the element itself — i.e.
 * whether a `text-overflow: ellipsis` clip is actually hiding something.
 *
 * By default the measurement is taken by `measure`, meant to be wired to the
 * element's `onMouseEnter`, rather than on every render or through a
 * `ResizeObserver`. A table can hold hundreds of truncating cells, and the
 * answer is usually only needed at the moment the pointer arrives, to decide
 * whether to open a tooltip. `hooks/useIsTruncated` is the vertical
 * counterpart, for content clamped to a number of lines.
 */
export default function useIsOverflowing<
  TElement extends HTMLElement = HTMLElement,
>({ measureOnMount = false }: Options = {}) {
  const ref = useRef<TElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const measure = useCallback(() => {
    const node = ref.current;
    /* istanbul ignore next -- @preserve */
    if (!node) return;

    setIsOverflowing(node.scrollWidth > node.clientWidth);
  }, []);

  useLayoutEffect(() => {
    if (measureOnMount) measure();
  }, [measureOnMount, measure]);

  return { ref, isOverflowing, measure };
}
